import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const agents = [
  {
    name: "Amy",
    role: "Receptionist",
    description: "Handles inbound calls 24/7 with zero patient wait times. Schedules directly into Asha PMS.",
    image: "/assets/amy.png",
    skills: ["Inbound Calls", "Scheduling", "Q&A"],
  },
  {
    name: "Leena",
    role: "Schedule Optimizer",
    description: "Prevents no-shows and fills cancellations with new patients to maximize production.",
    image: "/assets/leena.png",
    skills: ["Schedule Filling", "Confirmation"],
  },
  {
    name: "Stephanie",
    role: "Hygiene Recall",
    description: "Proactively calls unscheduled patients to book their hygiene appointments.",
    image: "/assets/stephanie.png",
    skills: ["Outbound Calls", "Reactivation"],
  },
  {
    name: "Elise",
    role: "Patient Experience",
    description: "Follows up with patients to ensure satisfaction and generate 5-star reviews.",
    image: "/assets/elise.png",
    skills: ["Feedback", "Reviews"],
  },
  {
    name: "Kim",
    role: "Case Acceptance",
    description: "Helps patients understand treatment plans and move forward with care.",
    image: "/assets/kim.png",
    skills: ["Treatment Pres.", "Education"],
  },
  {
    name: "Morgan",
    role: "Patient Intake",
    description: "Ensures patients complete intake forms and provide insurance info before they arrive.",
    image: "/assets/morgan.png",
    skills: ["Forms", "Insurance"],
  },
  {
    name: "Trisha",
    role: "Practice Manager",
    description: "Monitors all AI agents, assigns tasks to staff, and ensures nothing falls through the cracks.",
    image: "/assets/trisha.png",
    skills: ["Task Mgmt", "Monitoring"],
  },
  {
    name: "Joseph",
    role: "Fax Management",
    description: "Auto-sorts incoming faxes and attaches them to patient files in Asha PMS.",
    image: "/assets/joseph.png",
    skills: ["Sorting", "Filing"],
  },
];

const slugMap = {
  Amy: "amy",
  Leena: "leena",
  Stephanie: "stephanie",
  Elise: "elise",
  Kim: "kim",
  Morgan: "morgan",
  Trisha: "practice-manager",
  Joseph: "fax",
};

const PMSTeamIntro1Slide = ({ onAgentClick }) => {
  return (
    <div className="h-full w-full bg-slate-100 flex flex-col p-8">
      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3">
            <Sparkles className="w-4 h-4" />
            <span>Digital Workforce</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            8 AI Agents to Run Your{" "}
            <span className="text-blue-600">Front Office</span>.
          </h2>
          <p className="text-sm md:text-base text-slate-500 max-w-2xl mx-auto mt-2">
            From answering every call to managing your schedule, recalls, intake, and patient experience — all on autopilot.
          </p>
        </motion.div>
      </div>

      {/* Agent Cards Grid */}
      <div className="flex-grow flex items-center justify-center overflow-hidden">
        <div className="grid grid-cols-4 gap-4 w-full max-w-[1300px]">
          {agents.map((agent, index) => (
            <motion.div
              key={agent.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="h-full"
            >
              <div
                className="group/card relative bg-white rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col cursor-pointer h-[240px]"
                onClick={() => onAgentClick?.(slugMap[agent.name])}
              >
                {/* Agent Header */}
                <div className="p-4 pb-0 flex items-start justify-between">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-xl overflow-hidden shadow-sm ring-1 ring-slate-100">
                      <img
                        src={agent.image}
                        alt={agent.name}
                        className="w-full h-full object-cover transform group-hover/card:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentNode.style.backgroundColor = "#f1f5f9";
                        }}
                      />
                    </div>
                    <div
                      className="absolute -bottom-1 -right-1 bg-green-500 border-[2px] border-white w-4 h-4 rounded-full"
                      title="Online"
                    />
                  </div>
                  <div className="w-6 h-6 rounded-full bg-slate-50 group-hover/card:bg-blue-600 flex items-center justify-center transition-colors duration-300">
                    <ArrowRight className="w-3 h-3 text-slate-400 group-hover/card:text-white transition-colors" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 pt-3 flex-1 flex flex-col min-h-0">
                  <div className="mb-2">
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">
                      {agent.name},{" "}
                      <span className="text-blue-600 font-bold">
                        {agent.role}
                      </span>
                    </h3>
                  </div>

                  <p className="text-slate-600 text-xs leading-snug mb-3 line-clamp-3">
                    {agent.description}
                  </p>

                  {/* Skills Tags */}
                  <div className="flex flex-wrap gap-1 mt-auto">
                    {agent.skills.slice(0, 2).map((skill, i) => (
                      <span
                        key={i}
                        className="px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 text-[9px] font-bold border border-slate-100 uppercase tracking-wider"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hover highlight bar */}
                <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-indigo-500 transform scale-x-0 group-hover/card:scale-x-100 transition-transform duration-300 origin-left" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PMSTeamIntro1Slide;
